import { ElementRef } from "@angular/core";
import { FileInfo, FileRestrictions, UploadComponent } from "@progress/kendo-angular-upload";
import { DataResult, State } from "@progress/kendo-data-query";
import { forkJoin, Observable } from "rxjs";
import { DestroyBase } from "../../../../components/common/ng-destroy-base";
import { RefListModel } from "../../../../models/ref-list-model";
import { CalculationModelTemplate } from "../../models/calculation-model-template";
import { CalculationModelService } from "../../services/calculation-model-service";
import { EventBusService } from "../../../../services/event-bus.service";
import { AppLoadingIndicatorService } from "../../../../services/app-loading-indicator.service";

/**
 * Import groups
 */
export enum ImportGroupEnum {
  FlatFile = 1,
  BaringsFlatFile,
  Dcf,
  AxaFlatFile,
  AxaDcf,
  France
}

/**
 * Import groups translation mapping
 */
export const ImportGroupMapping = [
  { type: ImportGroupEnum.FlatFile, value: 'Flat File' },
  { type: ImportGroupEnum.BaringsFlatFile, value: 'Flat File' },
  { type: ImportGroupEnum.Dcf, value: 'other' },
  { type: ImportGroupEnum.AxaFlatFile, value: 'Flat File' },
  { type: ImportGroupEnum.AxaDcf, value: 'other' },
  { type: ImportGroupEnum.France, value: 'other' },
];

/**
 * Calculation model base class
 */
export class CalculationModelBase extends DestroyBase {
  /**
   * Should close?
   */
  shouldClose = false;

  /**
   * Template DTO
   */
  templateDto: CalculationModelTemplate;

  /**
   * Import groups
   */
  importGroups = ImportGroupMapping;

  /**
   * Template for import group
   */
  templateImportGroup: number = ImportGroupEnum.AxaDcf;

  /**
   * Newest emplate mapping version
   */
  newestTemplateMappingVersion: any;

  /**
   * The import files
   */
  importFiles: any[] = [];

  /**
   * Import GUIDs
   */
  importGuids: string[] = [];

  /**
   * Uploaded files
   */
  uploadedFiles: any[] = [];

  /**
   * Currency symbol
   */
  currencySymbol = '£';

  /**
   * Preview data
   */
  previewData: any = null;

  /**
   * Validation result
   */
  validationResult = null;

  /**
   * array as it will be displayed in grid
   */
  templatesList: CalculationModelTemplate[] = [];

  /**
   * Country ref list
   */
  countryRefList: RefListModel[] = [];

  /**
   * Status ref list
   */
  statusRefList: RefListModel[] = [];

  /**
   * Sector ref list
   */
  sectorRefList: RefListModel[] = [];

  /**
   * Valuation method ref list
   */
  valuationMethodRefList: RefListModel[] = [];

  /**
   * Mapping versions
   */
  mappingVersions: RefListModel[] = [];

  /**
   * Grid data
   */
  gridData: DataResult;

  /**
   * Grid state
   */
  gridState: State = {
    sort: [],
    skip: 0,
    take: 1,
  };

  /**
   * Steps count
   */
  stepsCount = 1;

  /**
   * Current step
   */
  currentStep = 1;

  /**
   * Maximum request size in bytes: 50MB
   */
  protected maximumRequestSize = 50 * 1024 * 1024;

  /**
   * Upload file restrictons: Only allow these types
   */
  uploadRestrictions: FileRestrictions = {
    maxFileSize: this.maximumRequestSize,
    allowedExtensions: [".xls", ".xlsx", ".xlsb"]
  };

  /**
   * onstructor
   * @param cmService
   * @param eventBus
   * @param elRef
   * @param loaderService
   */
  constructor(protected cmService: CalculationModelService,
    protected eventBus: EventBusService,
    protected elRef: ElementRef,
    protected loaderService: AppLoadingIndicatorService) {
    super();
  }

  /**
   * Load the ref lists
   */
  protected loadReflists(): Promise<void> {
    return new Promise((resolve, _reject) => {
      this.subscriptions.add(this.requestRefLists().subscribe(responseList => {
        this.countryRefList = responseList[0];
        this.statusRefList = responseList[1];
        this.sectorRefList = responseList[2];
        this.valuationMethodRefList = responseList[3];

        //get latest/newest mapping version
        this.mappingVersions = responseList[4];
        const ids = this.mappingVersions.map(item => item.oid);
        const maxId = Math.max(...ids);
        this.newestTemplateMappingVersion = this.mappingVersions.find(item => item.oid === maxId);

        resolve();
      }));
    });
  }

  /**
   * Show a loading indicator
   * @param show show/hide
   */
  protected showLoader(show: boolean) {
    if (show) {
      this.loaderService.show();
    }
    else {
      this.loaderService.hide();
    }
  }

  /**
   * Load the ref lists
   */
  private requestRefLists(): Observable<any[]> {
    const countryRefList = this.cmService.getRefList('ref_valuation_country');
    const statusRefList = this.cmService.getRefList('ref_calculation_model_template_status');
    const sectorRefList = this.cmService.getRefList('ref_calculation_model_template_sector');
    const valuationMethodRefList = this.cmService.getRefList('ref_calculation_model_template_valuation_method');
    const mappingVersionRefList = this.cmService.getRefList('ref_import_mapping_version');

    return forkJoin([countryRefList, statusRefList, sectorRefList, valuationMethodRefList, mappingVersionRefList]);
  }

  /**
   * File upload handler
   * @param upload UploadComponent
   * @param e upload event
   */
  onFileSelect(upload: UploadComponent, e) {
    e.preventDefault();
    if (e.files.length == 1) {
      upload.fileList.clear();
      const file = e.files[0];
      setTimeout(() => {
        if (file.size > this.maximumRequestSize) {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'maximumRequestSize');
        } else if (this.uploadRestrictions.allowedExtensions.indexOf(file.extension.toLowerCase()) === -1) {
          this.eventBus.broadcastEvent("openConfirmDialog", {
            success: () => {
              //Nothing
            }
          }, 'chooseExcelFile');
        } else {
          upload.fileList.add(file);

          for (const fileArr of upload.fileList.files) {
            const file: FileInfo = fileArr[0];
            const groupId = this.templateImportGroup;
            const mappingVersionId = this.newestTemplateMappingVersion.oid;
            this.readFile(file.rawFile, groupId, mappingVersionId).then((fileObj) => {
              this.importFiles = [fileObj];
            });
          }
        }
      }, 10);
    }
  }

  //#region Preview handling

  /**
   * Start pre-upload preview
   * @returns
   */
  async startPreuploadedImportPreview(): Promise<boolean> {
    //file is already uploaded
    const importFiles = this.uploadedFiles;
    const importPreviewRequest = {
      currencies: [{
        CurrencyCode: "GBP",
        CurrencyId: 4,
        CurrencyName: "Pound Sterling",
        DefaultUnitOfLength: "ft",
        DefaultUnitOfLengthId: 2,
        IsLeadCurrency: true,
        ValueInBaseCurrency: 1
      }],
      importFiles
    };

    this.showLoader(true);
    return new Promise((resolve, _reject) => {
      this.cmService.importPreview(importPreviewRequest).subscribe({
        next: (result: any) => {
          this.showLoader(false);
          this.onImportPreviewSuccess(result);
          setTimeout(() => {
            resolve(true);
          });
        },
        error: (_reason) => {
          this.showLoader(false);
          alert('Failed to generate preview!');
          resolve(false);
        }
      });
    });
  }

  /**
   * Start the import preview
   */
  onImportPreviewSuccess(response: any) {
    this.previewData = null;
    this.importGuids = [];
    if (response?.success) {
      this.validationResult = null;
      this.previewData = this.processPreviewResponse(response);
      this.importGuids = response.importGuids;
    }
    else {
      this.validationResult = response.validationResult;
    }
  }

  /**
   * Process the preview response
   */
  private processPreviewResponse(response: any) {
    const data = response.data;
    //remove empty imports (probably no data in import excel)
    const emptyImportsKeys = Object.keys(data).filter((key) => !data[key].DataRoot?.Data?.length);
    emptyImportsKeys.forEach((key) => delete data[key]);
    return response;
  }

  //#endregion

  //#region helpers
  /**
   * Reads a file as data-url
   * @param file
   * @param groupId
   * @param mappingVersionId
   * @returns
   */
  async readFile(file: File, groupId: number, mappingVersionId): Promise<any> {
    return new Promise<any>((resolve) => {
      const fileReader: FileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onloadend = (e) => {
        const dataUrl = e.target.result;
        resolve({
          fileName: file.name,
          dataUrl,
          groupId,
          size: file.size,
          mappingVersionId: mappingVersionId
        });
      }
    });
  }
  //#endregion helpers
}
